import React, { useEffect, useRef, useState } from 'react'
import store from './store'
import Login from './components/Login'
import { useAxios } from './hooks/useAxios'
import { getInitialized, setInitialized } from '@/store/slices/appSlice'
import {
  fetchConfigurationAsync,
  fetchGetUserDictionaryAsync,
} from '@/store/slices/userSlice'
import { fetchGetDataDictionaryAsync } from '@/store/slices/dataDictionarySlice'
import {
  fetchGetDepartmentTreeAsync,
  fetchGetDepartmentListAsync,
} from '@/store/slices/departmentSlice'
import { fetchGetMaterialTypeTreeAsync, fetchGetMaterialTypeTreeAllAsync } from '@/store/slices/materialTypeSlice'
import { fetchGetEmployeeDictionaryAsync } from '@/store/slices/hrmSlice'
import { useSelector } from 'react-redux'
import { Notification, Spin } from '@arco-design/web-react'
import { useLocation } from 'react-router-dom'
import { fetchGetMenuTreeAsync } from './store/slices/menuSlice'
import { fetchGetWorkshopsAsync } from './store/slices/workshopSlice'
import { getSetting } from '@/utils/settings'
import { useI18n } from '@/hooks/useLocale'

const KEY_TOKEN_LOGIN = getSetting('KEY_TOKEN_LOGIN')

function normalizeIgnoreAuthModulePath (pathname) {
  const normalizedPath = decodeURIComponent(pathname || '').replace(/\/+$/, '')
  if (!normalizedPath) return ''
  return `./pages${normalizedPath}`
}

export default function Auth (props) {
  const { children } = props
  const initialized = useSelector(getInitialized)
  const location = useLocation()
  const hasInitRef = useRef(false)
  const [ignoreAuthComponent, setIgnoreAuthComponent] = useState(null)
  const initAxios = useAxios()
  const t = useI18n()
  const dispatch = store.dispatch
  const isLoggedIn = !!localStorage.getItem(KEY_TOKEN_LOGIN)
  const ignoreAuth = new URLSearchParams(location.search).get('ignoreAuth') === 'true'
  const ignoreAuthModulePath = normalizeIgnoreAuthModulePath(location.pathname)

  useEffect(() => {
    if (!ignoreAuth) {
      setIgnoreAuthComponent(null)
      return undefined
    }

    if (!ignoreAuthModulePath) {
      setIgnoreAuthComponent(null)
      return undefined
    }

    let cancelled = false

    const loadIgnoreAuthComponent = async () => {
      try {
        const module = await import(`${ignoreAuthModulePath}`)
        if (!cancelled) {
          setIgnoreAuthComponent(() => module.default || module)
        }
      } catch (error) {
        console.error('load ignoreAuth component failed', error)
        if (!cancelled) {
          setIgnoreAuthComponent(null)
        }
      }
    }

    loadIgnoreAuthComponent()

    return () => {
      cancelled = true
    }
  }, [ignoreAuth, ignoreAuthModulePath])

  if (ignoreAuth) {
    if (!hasInitRef.current) {
      hasInitRef.current = true
      initAxios(isLoggedIn)
    }
    if (ignoreAuthComponent) {
      const IgnoreAuthComponent = ignoreAuthComponent
      return <IgnoreAuthComponent />
    }
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100vh',
        }}
      >
        <Spin dot tip={t('t_loading_data')} />
      </div>
    )
  }

  const isAuthRoute = /^\/(login|register)\/?$/.test(location.pathname)
  async function initStoreFetch () {
    const isDev = process.env.NODE_ENV === 'development'
    const proms = []
    if (isDev) {
      proms.push(await dispatch(fetchConfigurationAsync()))
      proms.push(await dispatch(fetchGetDataDictionaryAsync()))
      proms.push(await dispatch(fetchGetDepartmentTreeAsync()))
      proms.push(await dispatch(fetchGetDepartmentListAsync()))
      proms.push(await dispatch(fetchGetMaterialTypeTreeAsync()))
      proms.push(await dispatch(fetchGetMaterialTypeTreeAllAsync()))
      proms.push(await dispatch(fetchGetUserDictionaryAsync()))
      proms.push(await dispatch(fetchGetEmployeeDictionaryAsync()))
      proms.push(await dispatch(fetchGetMenuTreeAsync()))
      proms.push(await dispatch(fetchGetWorkshopsAsync()))
    } else {
      proms.push(dispatch(fetchConfigurationAsync()))
      proms.push(dispatch(fetchGetDataDictionaryAsync()))
      proms.push(dispatch(fetchGetDepartmentTreeAsync()))
      proms.push(dispatch(fetchGetDepartmentListAsync()))
      proms.push(dispatch(fetchGetMaterialTypeTreeAsync()))
      proms.push(dispatch(fetchGetMaterialTypeTreeAllAsync()))
      proms.push(dispatch(fetchGetUserDictionaryAsync()))
      proms.push(dispatch(fetchGetEmployeeDictionaryAsync()))
      proms.push(dispatch(fetchGetMenuTreeAsync()))
      proms.push(await dispatch(fetchGetWorkshopsAsync()))
    }

    Promise.all(proms)
      .then((x) => {
        let hasError = false
        if (x) {
          x.forEach((c) => {
            if (c?.error?.message) {
              hasError = true
              Notification.error({
                title: t('t_prompt'),
                content: c?.error?.message,
                position: 'bottomRight',
                duration: 0,
              })
            }
          })
        }
        hasError && console.error('缓存读取失败')
        !hasError && console.info('缓存读取成功')
      })
      .catch((err) => {
        console.log(err)
      })
      .finally(() => {
        dispatch(setInitialized(true))
      })
  }

  if (isAuthRoute) {
    if (!isLoggedIn) {
      initAxios(false)
    }
    return <Login />
  }

  if (isLoggedIn) {
    if (!initialized && !hasInitRef.current) {
      hasInitRef.current = true
      initAxios(true)
      initStoreFetch()
    }
  } else {
    initAxios(false)
    return <Login />
  }
  return initialized ? (
    children
  ) : (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100vh',
      }}
    >
      <Spin dot tip="正在加载数据" />
    </div>
  )
}

